import '@arcgis/map-components/components/arcgis-zoom';
// Only the map view needs the ArcGIS theme, so it's scoped here instead of loaded globally.
import '@arcgis/core/assets/esri/themes/light/main.css';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import type MapView from '@arcgis/core/views/MapView';
import type { ArcgisMap } from '@arcgis/map-components/components/arcgis-map';
import type { Listing, SaleType } from '@thevillages/schemas';

import {
  afterNextRender,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  type ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

import type { LayerState } from './update-listing-layers';

import { Environment } from '../../../environments/environment';
import { FilteringService } from '../../services/filtering';
import { StateService } from '../../services/state/state';
import { ApplyExtentFilter } from './apply-extent-filter';
import { BackgroundColor } from './graphics/background-color';
import { HandleViewClick } from './handle-click';
import { ClearHover, HandlePointerMove } from './handle-pointer-move';
import { BaseLayer } from './layers/baselayer';
import { SaleTypeLayer } from './layers/sale-type-layer';
import { SaleTypes } from './layers/sale-types';
import { ListingHoveredFromList } from './listing-hovered-from-list';
import { MapListingPopover } from './listing-popover/listing-popover';
import { ResetMapView } from './reset-map-view';
import { UpdateListingLayers } from './update-listing-layers';
import { WatchForExtentChanges } from './watch-for-extent-changes';

@Component({
  imports: [MapListingPopover],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-map-2',
  templateUrl: './map-2.html',
})
export class Map2 {
  protected readonly filteringService = inject(FilteringService);
  protected readonly stateService = inject(StateService);

  public readonly listings = input<Listing[]>([]);

  protected readonly mapElement = viewChild<ElementRef>('map');

  protected readonly backgroundColor = computed(() => BackgroundColor);
  protected readonly baseLayer = computed(BaseLayer);
  // Touch/coarse-pointer devices should not trigger hover interactions.
  protected readonly canHover =
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  protected readonly center = signal(Environment.mapCenter);
  protected readonly constraints = signal(Environment.mapConstraints);
  protected hoveredListingId?: string;
  protected readonly layerState = new Map<SaleType, LayerState>();
  protected readonly listingPopoverLeft = signal('0px');
  protected readonly listingPopoverTop = signal('0px');
  protected readonly mappedListings = computed(() => {
    const _map = new Map<string, Listing>();

    for (const _l of this.listings()) {
      _map.set(_l.id, _l);
    }

    return _map;
  });
  protected mapView?: MapView;
  // One clustered layer per sale type, keyed for quick lookup when applying listing updates.
  protected readonly saleTypeLayers = new Map<SaleType, FeatureLayer>();
  protected readonly zoom = signal(Environment.mapZoom);

  private readonly _pointerMove$ = new Subject<CustomEvent>();

  constructor() {
    // Import ArcGIS API for JavaScript only on the browser (support for SSR).
    afterNextRender(() => {
      void import('@arcgis/map-components/components/arcgis-map');
    });

    // Populate/diff the sale type layers whenever the filtered listings change.
    effect(() => {
      UpdateListingLayers.call(this);
    });

    // Reposition the popover when a listing is hovered from outside the map (e.g. the sidebar list).
    effect(() => {
      ListingHoveredFromList.call(this);
    });

    // Reflect filter-driven position changes (restored from the URL or a saved search) back onto the map.
    effect(() => {
      ApplyExtentFilter.call(this);
    });

    // Snap the map back to the default view when filters are reset.
    effect(() => {
      ResetMapView.call(this);
    });

    // Keep the filtering service (lat/lng/lvl/geoWithin) in sync with the current map viewport.
    effect((onCleanup) => {
      WatchForExtentChanges.call(this, onCleanup);
    });

    // Throttle hover hit-testing so frequent pointer moves stay lightweight.
    this._pointerMove$.pipe(throttleTime(50)).subscribe((event) => {
      HandlePointerMove.call(this, event);
    });
  }

  protected viewChanged(): void {
    ClearHover.call(this);
  }

  protected viewClicked(event: CustomEvent): void {
    if (!event.target) {
      return;
    }

    HandleViewClick.call(this, event);
  }

  protected viewPointerMove(event: CustomEvent<{ pointerType?: string }>): void {
    if (!event.target || !this.canHover || event.detail.pointerType === 'touch') {
      return;
    }

    this._pointerMove$.next(event);
  }

  protected viewReady(): void {
    const _mapEl = this.mapElement()?.nativeElement as ArcgisMap;
    if (!_mapEl.map || this.saleTypeLayers.size) {
      return;
    }

    for (const _saleType of SaleTypes) {
      this.saleTypeLayers.set(_saleType, SaleTypeLayer(_saleType));
      this.layerState.set(_saleType, { idToObjectId: new Map(), nextObjectId: 1 });
    }

    _mapEl.map.addMany([...this.saleTypeLayers.values()]);

    this.stateService.mapViewCreated.set(true);
  }
}
